import { useCallback, useState } from 'react'
import moment from 'moment-timezone'
import { useMutation } from 'urql'
import { SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { CANCEL_SCHEDULED_MESSAGE } from 'app/graphql/scheduledMessages'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { isSchedulable, resolveSendAt } from 'app/lib/scheduledSend'
import { logError } from 'lib/logging'

export type ScheduledSendTarget = {
  /** Recipients, snapshotted at schedule time rather than stored as a filter. */
  contactIds: string[]
  /** Sending location when every recipient shares one; omitted for a bulk run
   *  spanning practices, where each contact's own location sends. */
  locationId?: string
  /** The practice IANA zone. Absent means scheduling is unavailable. */
  timezone?: string
  /**
   * True when the recipient has opted out of SMS. Single-recipient surfaces
   * pass the contact's real opt-out state; a bulk target leaves it unset,
   * because opt-out there is a per-recipient skip the dispatcher applies
   * rather than a reason to withhold the whole control.
   */
  optedOut?: boolean
  /**
   * True when the AI agent is replying on this thread. Single-recipient
   * surfaces pass the thread's real state; a bulk target leaves it unset,
   * because there it is a per-recipient skip the run reports rather than a
   * reason to withhold the whole control.
   */
  agentHandling?: boolean
}

/**
 * Why the Schedule control cannot be offered, or undefined when it can.
 *
 * Split out of the hook so the rule is testable without mounting React, and so
 * every composer states the same reason.
 */
export function scheduleUnavailableReason(
  enabled: boolean,
  timezone: string | undefined,
  contactIds: string[],
  optedOut = false,
  agentHandling = false,
): string | undefined {
  if (!enabled) return `Scheduling is not enabled`
  if (!timezone || !moment.tz.zone(timezone)) {
    return `This location has no timezone set, so a send time can't be scheduled`
  }
  if (!contactIds.length) return `No recipients selected`
  // Last only because the three reasons above already hide the control, not
  // because it is the weaker rule. A control that offers to text someone who
  // replied STOP must not exist, even though the dispatcher re-checks opt-out
  // at fire time and would refuse the send anyway.
  if (optedOut) return `This number has opted out of SMS`
  // A queued message fires minutes or hours from now, into a thread the agent
  // may still be answering, and the two would reply to the patient separately.
  // The client cannot enforce this on its own: an agent can take a thread over
  // after a message is already queued, so the dispatcher has to refuse at fire
  // time too. That is TRK-5939.
  if (agentHandling) return `The AI Agent is replying on this thread`
  return undefined
}

/**
 * Create, reschedule, and cancel scheduled sends for one target.
 *
 * The client's time check is for the user's benefit at compose time only. The
 * dispatcher re-checks opt-out, quiet hours, and phone presence per recipient at
 * fire time and stays authoritative.
 */
export function useScheduledSend(target: ScheduledSendTarget) {
  const enabled = useFeatureToggle(`scheduled-sms`)
  const [scheduling, setScheduling] = useState(false)
  const [, sendSmsMutation] = useMutation(SEND_SMS_MUTATION)
  const [, cancelMutation] = useMutation(CANCEL_SCHEDULED_MESSAGE)

  const { contactIds, locationId, timezone, optedOut, agentHandling } = target
  const unavailableReason = scheduleUnavailableReason(enabled, timezone, contactIds, optedOut, agentHandling)
  // Everything except the recipient's own eligibility: the feature, the
  // practice clock, and the recipients. A surface that LISTS rows already
  // queued gates its read on this rather than on `unavailableReason`, because
  // an opt-out must stop new writes without hiding what is already waiting to
  // go out and still needs cancelling.
  const configured = !scheduleUnavailableReason(enabled, timezone, contactIds)

  // Serves single-recipient surfaces, so it schedules `contactIds[0]`. The
  // bulk path loops `sendSms` itself, because it needs a per-contact instant
  // and a per-row outcome.
  const schedule = useCallback(
    async (text: string, sendAtLocal: string) => {
      if (unavailableReason) return { ok: false, reason: unavailableReason }
      // `sendSms` requires a sending location. Guarded here rather than in
      // `scheduleUnavailableReason` because a bulk target legitimately omits
      // `locationId` (each contact's own location sends) and that path loops
      // `sendSms` itself; folding this into the reason would hide the control
      // from bulk instead of just refusing this single-recipient call.
      if (!locationId) return { ok: false, reason: `No sending location for this message` }
      if (!text.trim()) return { ok: false, reason: `Message is empty` }
      if (!isSchedulable(sendAtLocal, timezone!)) {
        return { ok: false, reason: `Pick a time between 8:00 AM and 8:00 PM at the practice` }
      }
      setScheduling(true)
      try {
        // The API takes an absolute instant; the wall clock is ours to resolve.
        const sendDate = resolveSendAt(sendAtLocal, timezone!).toISOString()
        const result = await sendSmsMutation({
          input: { contactId: contactIds[0], text, locationId, sendDate },
        })
        if (result.error || !result.data?.sendSms?.id) {
          const reason = result.error?.message ?? `Empty response from server`
          logError(`Could not schedule SMS`, reason)
          return { ok: false, reason }
        }
        return { ok: true }
      } finally {
        setScheduling(false)
      }
    },
    [unavailableReason, timezone, contactIds, locationId, sendSmsMutation],
  )

  const cancel = useCallback(
    async (id: string) => {
      const result = await cancelMutation({ id })
      if (result.error) {
        logError(`Could not cancel scheduled SMS`, result.error.message)
        return false
      }
      return true
    },
    [cancelMutation],
  )

  /**
   * Cancel a whole queued run. Reports both counts rather than a single
   * boolean, because a partial failure on four hundred messages is a real
   * outcome the user needs told about.
   */
  const cancelMany = useCallback(
    async (ids: string[]) => {
      const results = await Promise.all(ids.map((id) => cancel(id)))
      const cancelled = results.filter(Boolean).length
      return { cancelled, failed: results.length - cancelled }
    },
    [cancel],
  )

  /**
   * Move a queued message, or change its body.
   *
   * There is no update mutation, so this is a cancel plus a fresh send. That
   * ordering matters: cancelling first means a failure leaves nothing queued
   * rather than two copies queued, and the caller can retry.
   */
  const reschedule = useCallback(
    async (id: string, text: string, sendAtLocal: string) => {
      if (unavailableReason) return false
      if (!text.trim()) return false
      if (!isSchedulable(sendAtLocal, timezone!)) return false
      // `scheduling` has to cover the cancel step too, not just the send that
      // follows it. This is cancel-then-send, not one request, and a second
      // click landing in the gap between them can interleave two of these
      // pairs: two messages queued at once, breaking the one-per-thread cap,
      // or a cancel from the second click landing after the first click's
      // resend and leaving nothing queued at all.
      setScheduling(true)
      try {
        if (!(await cancel(id))) return false
        const result = await schedule(text, sendAtLocal)
        if (!result.ok) logError(`Rescheduled message was cancelled but not re-queued`, result.reason ?? ``)
        return result.ok
      } finally {
        setScheduling(false)
      }
    },
    [unavailableReason, timezone, cancel, schedule],
  )

  return {
    available: !unavailableReason,
    configured,
    unavailableReason,
    scheduling,
    schedule,
    cancel,
    cancelMany,
    reschedule,
  }
}
